import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { ICategorie } from 'src/app/shared/models/categorie';
import { ICompetence } from 'src/app/shared/models/competence';
import { IEvaluationResponse } from 'src/app/shared/models/evaluation';
import { IUniteOrganisationnelle } from 'src/app/shared/models/up-evaluation';
import { CompetenceService } from '../../competence-list/competence.service';
import { UniteOrganisationnelleService } from '../../uo-evaluation/uo-evaluation.service';
import { EvaluationService } from '../evaluation-service';

export interface UoSection {
  selectedUoId: string | null;
  selectedCategorieId: string | null;
  uoLibelle: string;
  categoriesFiltrees: ICategorie[];
  competencesFiltrees: ICompetence[];
  allCompetencesUo: ICompetence[];
}

@Component({
  selector: 'app-evaluation-form',
  templateUrl: './evaluation-form.component.html',
  styleUrls: ['./evaluation-form.component.less']
})
export class EvaluationFormComponent implements OnInit, OnDestroy {

  step1Form!: UntypedFormGroup;
  isSubmitting = false;
  backendError: string | null = null;

  unitesOrganisationnelles: IUniteOrganisationnelle[] = [];
  applications: string[] = [];
  createdBy = '';

  // UO principale — charge tout automatiquement
  uoPrincipaleId: string | null = null;
  loadingUoPrincipale = false;

  // Sections UO additionnelles
  uoSections: UoSection[] = [];

  // Liste finale des compétences sélectionnées (toutes sections confondues)
  competencesSelectionnees: ICompetence[] = [];

  // Empêche la création tant que l'utilisateur n'a pas parcouru les 3 étapes
  step3Visited = false;
  private destroy$ = new Subject<void>();

  constructor(
    private _fb: UntypedFormBuilder,
    private evaluationService: EvaluationService,
    private uoService: UniteOrganisationnelleService,
    private competenceService: CompetenceService,
    private authService: AuthenticationService,
    private http: HttpClient,
    private snackbarService: SnackbarService,
    private currentUserService: CurrentUserService,
    public dialogRef: MatDialogRef<EvaluationFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: null
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.step1Form = this._fb.group({
      nom:          ['', [Validators.required, Validators.maxLength(120)]],
      prenom:       ['', [Validators.required, Validators.maxLength(120)]],
      application:  [null, Validators.required],
      dateDemarrage:[null, Validators.required],
      dateFinMonteeCompetence:[null, Validators.required]
    });

    // Sections additionnelles vides au départ (l'UO principale charge tout automatiquement)
    this.uoSections = [];

    this.loadReferenceData();
  }

  // ── UO principale — charge automatiquement TOUTES les compétences ────────

  onUoPrincipaleChange(): void {
    // Réinitialiser la sélection précédente issue de l'UO principale
    this.competencesSelectionnees = [];

    if (!this.uoPrincipaleId) return;

    this.loadingUoPrincipale = true;
    this.competenceService.getActiveByUo(this.uoPrincipaleId).subscribe({
      next: (competences) => {
        // Toutes les compétences de l'UO principale sont ajoutées automatiquement
        this.competencesSelectionnees = [...competences];
        this.loadingUoPrincipale = false;
      },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des compétences');
        this.loadingUoPrincipale = false;
      }
    });
  }

  // ── Sections UO ───────────────────────────────────────────────────────────

  newSection(): UoSection {
    return {
      selectedUoId: null,
      selectedCategorieId: null,
      uoLibelle: '',
      categoriesFiltrees: [],
      competencesFiltrees: [],
      allCompetencesUo: []
    };
  }

  addUoSection(): void {
    this.uoSections.push(this.newSection());
  }

  removeUoSection(index: number): void {
    this.uoSections.splice(index, 1);
  }

  // ── UOs déjà utilisées (UO principale + sections) — exclues des selects ──

  getAvailableUos(currentSection: UoSection): IUniteOrganisationnelle[] {
    const usedIds = new Set<string>();
    if (this.uoPrincipaleId) usedIds.add(this.uoPrincipaleId);

    this.uoSections.forEach(s => {
      // On exclut toutes les UOs déjà choisies SAUF celle de la section courante
      if (s !== currentSection && s.selectedUoId) {
        usedIds.add(s.selectedUoId);
      }
    });

    return this.unitesOrganisationnelles.filter(uo => !usedIds.has(uo.id));
  }

  // ── Changement UO → charge catégories + compétences ──────────────────────

  onUoChange(section: UoSection): void {
    section.selectedCategorieId = null;
    section.categoriesFiltrees = [];
    section.competencesFiltrees = [];
    section.allCompetencesUo = [];

    const uo = this.unitesOrganisationnelles.find(u => u.id === section.selectedUoId);
    section.uoLibelle = uo ? `${(uo as any).code} — ${(uo as any).libelle}` : '';

    if (!section.selectedUoId) return;

    this.competenceService.getActiveByUo(section.selectedUoId).subscribe({
      next: (competences) => {
        section.allCompetencesUo = competences;

        // Déduire les catégories depuis les compétences
        const catMap = new Map<string, ICategorie>();
        competences.forEach(c => {
          if (!catMap.has(c.categorieId)) {
            catMap.set(c.categorieId, { id: c.categorieId, nom: c.categorieNom });
          }
        });
        section.categoriesFiltrees = Array.from(catMap.values());
      },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des compétences');
      }
    });
  }

  onCategorieChange(section: UoSection): void {
    section.competencesFiltrees = section.allCompetencesUo.filter(
      c => c.categorieId === section.selectedCategorieId
    );
  }

  // ── Gestion des compétences sélectionnées ─────────────────────────────────

  addCompetence(comp: ICompetence): void {
    if (!this.isAlreadyAdded(comp.id)) {
      this.competencesSelectionnees.push(comp);
    }
  }

  removeCompetence(id: string): void {
    this.competencesSelectionnees = this.competencesSelectionnees.filter(c => c.id !== id);
  }

  isAlreadyAdded(id: string): boolean {
    return this.competencesSelectionnees.some(c => c.id === id);
  }

  // ── Chargement des référentiels ───────────────────────────────────────────

  loadReferenceData(): void {
    const user = this.currentUserService.getCurrentUser();
    this.createdBy = user?.identifier ?? '';
    const isAdminOrServiceManager = this.authService.isUserAdminOrServiceManagerOrManagerCDS();

    forkJoin({
      uos: this.uoService.getAll(),
      projects: this.http.get<any[]>('/api/v1/projects')
    }).subscribe({
      next: ({ uos, projects }) => {
        this.unitesOrganisationnelles = uos;
        if (isAdminOrServiceManager) {
          const allApps = projects.map((p: any) => p.name).filter(Boolean);
          this.applications = [...new Set<string>(allApps)].sort();
        } else {
          this.applications = this.currentUserService.getUserApplications().map((a: any) => a.name).filter(Boolean);
        }
      },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des données');
      }
    });
  }

  // ── Suivi de progression du stepper ───────────────────────────────────────

  onStepChange(event: any): void {
    // index 2 = étape 3 (Récapitulatif), 0-indexed
    if (event.selectedIndex === 2) {
      this.step3Visited = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.step1Form.invalid || this.competencesSelectionnees.length === 0) return;

    this.backendError = null;
    this.isSubmitting = true;

    const { nom, prenom, application, dateDemarrage, dateFinMonteeCompetence } = this.step1Form.value;

    // UO principale = celle choisie en premier dans l'étape 2
    const primaryUoId = this.uoPrincipaleId ?? this.uoSections[0]?.selectedUoId ?? '';

    this.evaluationService.createWithCompetences({
      nom,
      prenom,
      application,
      uniteOrganisationnelleId: primaryUoId,
      dateDemarrage: this.formatDate(dateDemarrage)!,
      dateFinMonteeCompetence: this.formatDate(dateFinMonteeCompetence)!,
      createdBy: this.createdBy,
      competenceIds: this.competencesSelectionnees.map(c => c.id)
    }).subscribe({
      next: (result: IEvaluationResponse) => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar('Évaluation créée avec succès');
        this.dialogRef.close(result);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, 'Erreur lors de la création');
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private formatDate(d: Date | string | null): string | undefined {
    if (!d) return undefined;
    return new Date(d).toISOString().split('T')[0];
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}